import { useCallback, useEffect, useState } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";

import { fetchWithAuth } from "@/lib/utils";

/** These three states are the complete value-free status surface from the API. */
export type WorkspaceSkillWiringStatus = "wired" | "unwired" | "unknown";
export type WorkspaceSkillSecretScope = "personal" | "organization";
/** Loader-stamped trust from the agent's effective workspace source. */
export type WorkspaceSkillTrust = "trusted" | "untrusted";

export interface WorkspaceSkillWiringRequirement {
  /** Opaque, server-issued handle. It must only be sent back to this API. */
  requirement_id: string;
  /** The one safe-to-display identifier for this requirement. */
  env: string;
  status: WorkspaceSkillWiringStatus;
  source: "postgres" | null;
}

export interface WorkspaceSkillWiringSummary {
  /** Opaque, server-issued handle. It must only be sent back to this API. */
  workspace_skill_id: string;
  name: string;
  /** Optional display-safe frontmatter copy from newer workspace catalogs. */
  description?: string | null;
  origin: "workspace";
  trust: WorkspaceSkillTrust;
  status: WorkspaceSkillWiringStatus;
  required: number;
  resolved: number;
  requirements: WorkspaceSkillWiringRequirement[];
}

export interface WorkspaceSkillsWiringData {
  /** Includes active and disabled optional skills selected by the backend. */
  skills: WorkspaceSkillWiringSummary[];
}

export type WorkspaceSkillWiringError =
  | "authentication"
  | "forbidden"
  | "skill_changed"
  | "not_found"
  | "unavailable"
  | "invalid_request"
  | "invalid_response"
  | "network";

export interface UseWorkspaceSkillWiringReturn {
  data: WorkspaceSkillsWiringData | null;
  loading: boolean;
  error: WorkspaceSkillWiringError | null;
  /** True only when the catalog route itself is absent on an older server. */
  unsupported: boolean;
  refresh: () => Promise<void>;
  saveWorkspaceSkillSecret: (
    workspaceSkillId: string,
    requirementId: string,
    value: string,
    scope: WorkspaceSkillSecretScope,
  ) => Promise<boolean>;
  deleteWorkspaceSkillSecret: (
    workspaceSkillId: string,
    requirementId: string,
    scope: WorkspaceSkillSecretScope,
  ) => Promise<boolean>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isWiringStatus(value: unknown): value is WorkspaceSkillWiringStatus {
  return value === "wired" || value === "unwired" || value === "unknown";
}

function isWorkspaceSkillTrust(value: unknown): value is WorkspaceSkillTrust {
  return value === "trusted" || value === "untrusted";
}

function parseRequirement(value: unknown): WorkspaceSkillWiringRequirement | null {
  if (!isRecord(value)) return null;
  if (
    typeof value.requirement_id !== "string"
    || !value.requirement_id
    || typeof value.env !== "string"
    || !value.env
    || !isWiringStatus(value.status)
    || (value.source !== "postgres" && value.source !== null)
  ) {
    return null;
  }

  return {
    requirement_id: value.requirement_id,
    env: value.env,
    status: value.status,
    source: value.source,
  };
}

function parseSkill(value: unknown): WorkspaceSkillWiringSummary | null {
  if (!isRecord(value) || !Array.isArray(value.requirements)) return null;
  const trust = value.trust;
  if (
    typeof value.workspace_skill_id !== "string"
    || !value.workspace_skill_id
    || typeof value.name !== "string"
    || !value.name
    || (value.description !== undefined
      && value.description !== null
      && typeof value.description !== "string")
    || value.origin !== "workspace"
    // Older servers send only "untrusted"; accept it alongside the trusted
    // label introduced by newer workspace-skill catalogs.
    || !isWorkspaceSkillTrust(trust)
    || !isWiringStatus(value.status)
    || typeof value.required !== "number"
    || typeof value.resolved !== "number"
  ) {
    return null;
  }

  const requirements = value.requirements.map(parseRequirement);
  if (requirements.some((requirement) => requirement === null)) return null;

  return {
    workspace_skill_id: value.workspace_skill_id,
    name: value.name,
    ...(value.description !== undefined && { description: value.description }),
    origin: value.origin,
    trust,
    status: value.status,
    required: value.required,
    resolved: value.resolved,
    requirements: requirements as WorkspaceSkillWiringRequirement[],
  };
}

function parseCatalog(value: unknown): WorkspaceSkillsWiringData | null {
  if (!isRecord(value) || !Array.isArray(value.skills)) return null;
  const skills = value.skills.map(parseSkill);
  if (skills.some((skill) => skill === null)) return null;
  return { skills: skills as WorkspaceSkillWiringSummary[] };
}

function errorForStatus(status: number): WorkspaceSkillWiringError {
  if (status === 401) return "authentication";
  if (status === 403) return "forbidden";
  if (status === 409) return "skill_changed";
  if (status === 404) return "not_found";
  if (status >= 500) return "unavailable";
  return "invalid_request";
}

function basePath(agentId: string): string {
  return `/api/connections/agents/${encodeURIComponent(agentId)}/workspace-skills`;
}

/**
 * Value-free wiring for the workspace skills owned by one agent.
 *
 * The API is intentionally handle-based: this hook neither derives nor
 * displays a secret path, logical secret name, or a returned secret value.
 */
export function useWorkspaceSkillWiring(
  agentId: string,
  getToken: () => Promise<string>,
): UseWorkspaceSkillWiringReturn {
  const [data, setData] = useState<WorkspaceSkillsWiringData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<WorkspaceSkillWiringError | null>(null);
  const [unsupported, setUnsupported] = useState(false);

  const refresh = useCallback(async () => {
    if (!agentId) {
      setData(null);
      setError(null);
      setUnsupported(false);
      return;
    }

    setLoading(true);
    setError(null);
    setUnsupported(false);

    try {
      const response = await fetchWithAuth(basePath(agentId), { headers: {} }, getToken);
      // A missing catalog route is the explicit compatibility signal for an
      // independently deployed older agno-server. Do not treat it as an empty
      // catalog, because that would suggest there are no workspace skills.
      if (response.status === 404) {
        setData(null);
        setUnsupported(true);
        return;
      }
      if (!response.ok) {
        setData(null);
        setError(errorForStatus(response.status));
        return;
      }

      let body: unknown;
      try {
        body = await response.json();
      } catch {
        setData(null);
        setError("invalid_response");
        return;
      }

      const catalog = parseCatalog(body);
      if (!catalog) {
        setData(null);
        setError("invalid_response");
        return;
      }
      setData(catalog);
    } catch {
      // Never surface a thrown response/body here: it could be unexpected
      // server content, while the UI only needs a safe actionable message.
      setData(null);
      setError("network");
    } finally {
      setLoading(false);
    }
  }, [agentId, getToken]);

  const saveWorkspaceSkillSecret = useCallback(
    async (
      workspaceSkillId: string,
      requirementId: string,
      value: string,
      scope: WorkspaceSkillSecretScope,
    ): Promise<boolean> => {
      setError(null);
      try {
        const response = await fetchWithAuth(
          `${basePath(agentId)}/${encodeURIComponent(workspaceSkillId)}/secrets`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            // The value is write-only. Do not parse or retain the response.
            body: JSON.stringify({ requirement_id: requirementId, value, scope }),
          },
          getToken,
        );
        if (!response.ok) {
          setError(errorForStatus(response.status));
          return false;
        }

        await refresh();
        return true;
      } catch {
        setError("network");
        return false;
      }
    },
    [agentId, getToken, refresh],
  );

  const deleteWorkspaceSkillSecret = useCallback(
    async (
      workspaceSkillId: string,
      requirementId: string,
      scope: WorkspaceSkillSecretScope,
    ): Promise<boolean> => {
      setError(null);
      try {
        const query = new URLSearchParams({ requirement_id: requirementId, scope });
        const response = await fetchWithAuth(
          `${basePath(agentId)}/${encodeURIComponent(workspaceSkillId)}/secrets?${query.toString()}`,
          { method: "DELETE", headers: {} },
          getToken,
        );
        if (!response.ok) {
          setError(errorForStatus(response.status));
          return false;
        }

        await refresh();
        return true;
      } catch {
        setError("network");
        return false;
      }
    },
    [agentId, getToken, refresh],
  );

  useRefreshOnReconnect(refresh);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    data,
    loading,
    error,
    unsupported,
    refresh,
    saveWorkspaceSkillSecret,
    deleteWorkspaceSkillSecret,
  };
}
